import React, { useEffect, useState, useCallback } from 'react';
import { TrustJetPlane, getAircraftImage } from '@assets/images';

/**
 * Resilient aircraft image with a 3-level fallback chain:
 *   1. The provided `src` (or `aircraft.image_urls[0]` if src is omitted)
 *   2. The make_model bundled image (from aircraftImages dictionary)
 *   3. TrustJetPlane placeholder (bgplaceholdernew.jpg)
 *   4. Neutral gray div (only if even step 3 fails — extremely unlikely)
 *
 * Progression is driven by <img>'s onError, so each broken URL only
 * transitions once per mount. When `src` changes (e.g. carousel navigation
 * upstream), the chain resets to step 0.
 *
 * Uses forwardRef so callers can still grab the underlying <img> DOM
 * element (e.g. for html2canvas capture in the contract signing PDF).
 */
const AircraftImage = React.forwardRef(function AircraftImage(
  { aircraft, src, alt = 'Aircraft', className = '', style, ...rest },
  ref
) {
  // Prefer explicit src prop; otherwise use the aircraft's first image_url
  const initialSrc =
    src ||
    (Array.isArray(aircraft?.image_urls) && aircraft.image_urls[0]) ||
    null;

  const [step, setStep] = useState(0);           // 0=initial, 1=make_model, 2=TrustJetPlane, 3=give-up
  const [currentSrc, setCurrentSrc] = useState(initialSrc);

  // Reset chain whenever the caller passes a new src (or aircraft changes).
  useEffect(() => {
    setStep(0);
    setCurrentSrc(initialSrc);
  }, [initialSrc]);

  const handleError = useCallback(() => {
    // Step 0 → 1 (or 0 → 2 if no make_model match)
    if (step === 0) {
      const makeModel =
        aircraft?.aircraft_make_model?.make_model ||
        aircraft?.make_model ||
        aircraft?.makeModel;
      const modelSrc = makeModel ? getAircraftImage(makeModel) : null;
      if (modelSrc && modelSrc !== currentSrc) {
        setCurrentSrc(modelSrc);
        setStep(1);
        return;
      }
      // No usable make_model image — jump directly to step 2
      setCurrentSrc(TrustJetPlane);
      setStep(2);
      return;
    }
    // Step 1 → 2 (fall back to TrustJetPlane)
    if (step === 1) {
      setCurrentSrc(TrustJetPlane);
      setStep(2);
      return;
    }
    // Step 2 → 3 (give up — bundled asset failed, which shouldn't happen)
    setStep(3);
  }, [step, aircraft, currentSrc]);

  if (step >= 3 || !currentSrc) {
    return (
      <div
        ref={ref}
        className={`bg-gray-100 ${className}`}
        style={style}
        aria-label={alt}
      />
    );
  }

  return (
    <img
      ref={ref}
      src={currentSrc}
      alt={alt}
      onError={handleError}
      className={className}
      style={style}
      {...rest}
    />
  );
});

export default AircraftImage;
